/**
 * Card "Portal cliente" — gestión de credenciales del portal mi.bytecom.mx
 * para un cliente específico desde el panel admin.
 *
 * Estados:
 *  - Sin credencial → botón "Generar credencial"
 *  - Con credencial activa → estado + botón "Regenerar" + botón "Revocar"
 *  - Con credencial revocada → botón "Reactivar" (que regenera)
 *
 * Al generar/regenerar, abre un modal con la password en CLARO una sola vez
 * con botón "Copiar" — el operador la imprime en el contrato.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Copy, Check, AlertTriangle, RefreshCw, ShieldOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiCall } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";

type CredencialStatus = {
  cliente_id: string;
  has_credential: boolean;
  must_change_password?: boolean;
  last_login_at?: string | null;
  last_login_ip?: string | null;
  last_password_change_at?: string | null;
  failed_login_count?: number;
  locked_until?: string | null;
  revoked_at?: string | null;
  created_at?: string | null;
};

type GenerarResp = {
  cliente_id: string;
  numero_cliente: string;
  password_temporal: string;
  must_change_password: boolean;
  portal_url: string;
};

export function PortalCredencialesCard({
  clienteId,
  numeroCliente,
}: {
  clienteId: string;
  numeroCliente: string;
}) {
  const qc = useQueryClient();
  const [showResult, setShowResult] = useState<GenerarResp | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const { data: status, isLoading } = useQuery<CredencialStatus>({
    queryKey: ["cliente", clienteId, "portal-credentials"],
    queryFn: () =>
      apiCall<CredencialStatus>(
        "get",
        `/api/v1/clientes/${clienteId}/portal-credentials`,
      ),
  });

  const generarMut = useMutation({
    mutationFn: () =>
      apiCall<GenerarResp>(
        "post",
        `/api/v1/clientes/${clienteId}/portal-credentials`,
      ),
    onSuccess: (resp) => {
      setShowResult(resp);
      setConfirmRegen(false);
      qc.invalidateQueries({
        queryKey: ["cliente", clienteId, "portal-credentials"],
      });
      toast.success("Credencial generada");
    },
    onError: (e: any) => {
      toast.error(e?.message || "No se pudo generar la credencial");
    },
  });

  const revocarMut = useMutation({
    mutationFn: () =>
      apiCall("delete", `/api/v1/clientes/${clienteId}/portal-credentials`),
    onSuccess: () => {
      setConfirmRevoke(false);
      qc.invalidateQueries({
        queryKey: ["cliente", clienteId, "portal-credentials"],
      });
      toast.success("Acceso revocado");
    },
    onError: (e: any) => {
      toast.error(e?.message || "No se pudo revocar");
    },
  });

  const isLocked =
    status?.locked_until && new Date(status.locked_until) > new Date();
  const isRevoked = !!status?.revoked_at;
  const hasCred = !!status?.has_credential;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound size={14} />
            Portal cliente
            {hasCred && !isRevoked && !isLocked && (
              <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
                Activo
              </Badge>
            )}
            {isLocked && (
              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
                Bloqueado temporal
              </Badge>
            )}
            {isRevoked && (
              <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">
                Revocado
              </Badge>
            )}
            {!hasCred && (
              <Badge variant="outline" className="text-ink-700 border-ink-200 bg-ink-50">
                Sin credencial
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Cargando…</p>
          ) : !hasCred ? (
            <div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                Este cliente aún no tiene acceso al portal{" "}
                <span className="font-mono">mi.bytecom.mx</span>. Genera una
                credencial para que pueda consultar sus facturas y datos.
              </p>
              <Button
                onClick={() => generarMut.mutate()}
                disabled={generarMut.isPending}
                size="sm"
              >
                <KeyRound size={12} className="mr-1.5" />
                Generar credencial
              </Button>
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-[140px_1fr] gap-y-1.5 text-xs">
                <dt className="text-muted-foreground">Número de cliente</dt>
                <dd className="font-mono">{numeroCliente}</dd>
                {status?.created_at && (
                  <>
                    <dt className="text-muted-foreground">Generada</dt>
                    <dd>{formatDate(status.created_at)}</dd>
                  </>
                )}
                {status?.must_change_password && (
                  <>
                    <dt className="text-muted-foreground">Estado</dt>
                    <dd className="text-amber-700 inline-flex items-center gap-1">
                      <AlertTriangle size={11} /> Pendiente cambio inicial
                    </dd>
                  </>
                )}
                {status?.last_login_at && (
                  <>
                    <dt className="text-muted-foreground">Último login</dt>
                    <dd>
                      {formatDate(status.last_login_at)}
                      {status.last_login_ip && (
                        <span className="ml-1 text-muted-foreground">
                          ({status.last_login_ip})
                        </span>
                      )}
                    </dd>
                  </>
                )}
                {status?.failed_login_count != null &&
                  status.failed_login_count > 0 && (
                    <>
                      <dt className="text-muted-foreground">Fallos recientes</dt>
                      <dd>{status.failed_login_count}</dd>
                    </>
                  )}
                {isLocked && status?.locked_until && (
                  <>
                    <dt className="text-muted-foreground">Bloqueado hasta</dt>
                    <dd className="text-amber-700">
                      {formatDate(status.locked_until)}
                    </dd>
                  </>
                )}
                {isRevoked && status?.revoked_at && (
                  <>
                    <dt className="text-muted-foreground">Revocado</dt>
                    <dd className="text-red-700">
                      {formatDate(status.revoked_at)}
                    </dd>
                  </>
                )}
              </dl>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmRegen(true)}
                  disabled={generarMut.isPending}
                >
                  <RefreshCw size={12} className="mr-1.5" />
                  {isRevoked ? "Reactivar (genera nueva)" : "Regenerar password"}
                </Button>
                {!isRevoked && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => setConfirmRevoke(true)}
                    disabled={revocarMut.isPending}
                  >
                    <ShieldOff size={12} className="mr-1.5" />
                    Revocar acceso
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal con password generado — UNA VEZ */}
      <ResultDialog
        result={showResult}
        onClose={() => setShowResult(null)}
      />

      {/* Confirm regenerar */}
      <Dialog open={confirmRegen} onOpenChange={setConfirmRegen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerar password del portal</DialogTitle>
            <DialogDescription>
              La password actual del cliente quedará invalidada. Se generará
              una nueva que tendrás que entregarle. ¿Continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRegen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => generarMut.mutate()}
              disabled={generarMut.isPending}
            >
              {generarMut.isPending ? "Generando..." : "Sí, regenerar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm revocar */}
      <Dialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revocar acceso al portal</DialogTitle>
            <DialogDescription>
              El cliente ya no podrá entrar a {" "}
              <span className="font-mono">mi.bytecom.mx</span>. Cualquier
              sesión activa también queda invalidada en el siguiente request.
              Puedes reactivar el acceso después regenerando la password.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => revocarMut.mutate()}
              disabled={revocarMut.isPending}
            >
              {revocarMut.isPending ? "Revocando..." : "Sí, revocar acceso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal con la password en CLARO una sola vez
// ─────────────────────────────────────────────────────────────────────────────

function ResultDialog({
  result,
  onClose,
}: {
  result: GenerarResp | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<"pwd" | "all" | null>(null);

  if (!result) return null;

  const copy = (kind: "pwd" | "all") => {
    const text =
      kind === "pwd"
        ? result.password_temporal
        : `Portal: ${result.portal_url}\nNúmero de cliente: ${result.numero_cliente}\nContraseña temporal: ${result.password_temporal}\n\nDeberá cambiarla en el primer login.`;
    navigator.clipboard.writeText(text);
    setCopied(kind);
    toast.success(kind === "pwd" ? "Password copiado" : "Datos copiados");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check size={16} className="text-emerald-600" />
            Credencial generada
          </DialogTitle>
          <DialogDescription>
            Esta password aparece <strong>una sola vez</strong>. Cópiala o
            imprímela en el contrato del cliente — no podrás verla de nuevo
            (en BD se guarda solo el hash).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          <Field label="URL del portal" value={result.portal_url} mono />
          <Field label="Número de cliente" value={result.numero_cliente} mono />

          <div>
            <div className="text-2xs uppercase tracking-wide text-muted-foreground mb-1.5 font-medium">
              Contraseña temporal
            </div>
            <div className="flex gap-1.5">
              <code className="flex-1 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm font-mono text-amber-900 select-all">
                {result.password_temporal}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy("pwd")}
                title="Copiar password"
              >
                {copied === "pwd" ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
            <div className="text-2xs text-muted-foreground mt-1.5">
              El cliente deberá cambiarla en su primer login.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => copy("all")}>
            <Copy size={12} className="mr-1.5" />
            Copiar todo
          </Button>
          <Button onClick={onClose}>Listo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-wide text-muted-foreground mb-1 font-medium">
        {label}
      </div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
